import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Paper,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  SignalCellularAlt as SignalCellularAltIcon,
} from '@mui/icons-material';
import { useGetCommandsQuery, useRetryCommandMutation, useCancelCommandMutation } from '../../store/api/commandApi';
import type { Command, Device } from '../../types/api';
import SendCommandDialog from './SendCommandDialog';
import DPSEnvironmentDialog from './DPSEnvironmentDialog';
import LteApnDialog from './LteApnDialog';
import { useWebSocket } from '@hooks/useWebSocket';
import { fontFamilyMono } from '../../theme/theme';

interface DeviceCommandsProps {
  deviceId: string;
  device?: Device;
}

const DeviceCommands: React.FC<DeviceCommandsProps> = ({ deviceId, device }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<Command['status'] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sendCommandOpen, setSendCommandOpen] = useState(false);
  const [dpsEnvironmentOpen, setDpsEnvironmentOpen] = useState(false);
  const [lteApnOpen, setLteApnOpen] = useState(false);

  // Fetch command data from API with auto-refresh for pending/executing commands
  // Real-time updates are handled via WebSocket (see useEffect below)
  const { data, isLoading, error, refetch, fulfilledTimeStamp } = useGetCommandsQuery({
    deviceId,
    status: statusFilter || undefined,
    search: searchQuery || undefined,
    page: page + 1, // API uses 1-based pagination
    pageSize: rowsPerPage,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  }, {
    // No polling - rely on WebSocket for real-time updates
    // Refetch when window regains focus as fallback
    refetchOnFocus: true,
    // Keep data fresh for 30 seconds as secondary fallback
    refetchOnMountOrArgChange: 30,
  });

  const [retryCommand] = useRetryCommandMutation();
  const [cancelCommand] = useCancelCommandMutation();

  // Set up WebSocket for real-time command status updates
  // This eliminates the need for polling and provides instant feedback
  const { on, emit, connected } = useWebSocket({
    autoConnect: true,
    events: {
      device_update: (data: any) => {
        // Handle command status updates for this device
        if (data.device_id === deviceId && data.update_type === 'command_status') {
          console.log('🔄 WebSocket: Command status update received (device_update)', data);
          // Refetch commands immediately to get the latest status
          refetch();
        }
      },
      command_update: (data: any) => {
        // Handle direct command updates (sent from heartbeat endpoint)
        if (data.device_id === deviceId) {
          console.log('🔄 WebSocket: Command update received (command_update)', data);
          // Refetch commands immediately to get the latest status
          refetch();
        }
      },
      subscription: (data: any) => {
        // Log subscription confirmations from server
        console.log('✅ WebSocket: Subscription confirmed', data);
      }
    }
  });

  // Subscribe to device updates when WebSocket connects
  useEffect(() => {
    if (connected && deviceId) {
      console.log(`📡 WebSocket: Subscribing to device ${deviceId}`);
      // Small delay to ensure WebSocket is fully ready
      const timer = setTimeout(() => {
        emit('subscribe', { device_id: deviceId });
      }, 100);

      // Cleanup function - only unsubscribe when unmounting or device changes
      return () => {
        clearTimeout(timer);
        if (connected) {
          console.log(`📡 WebSocket: Unsubscribing from device ${deviceId}`);
          emit('unsubscribe', { device_id: deviceId });
        }
      };
    }
  }, [connected, deviceId, emit]);

  // Listen for legacy command_status_update events (backward compatibility)
  useEffect(() => {
    const unsubscribe = on('command_status_update', (data: any) => {
      if (data.device_id === deviceId) {
        console.log('🔄 WebSocket: Legacy command status update received', data);
        // Refetch to get updated command status
        refetch();
      }
    });

    return unsubscribe;
  }, [deviceId, on, refetch]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleStatusFilter = (status: Command['status'] | null) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpanded = (commandId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(commandId)) {
      newExpanded.delete(commandId);
    } else {
      newExpanded.add(commandId);
    }
    setExpandedRows(newExpanded);
  };

  const handleRetry = async (commandId: string) => {
    try {
      await retryCommand(commandId).unwrap();
    } catch (err) {
      console.error('Failed to retry command:', err);
    }
  };

  const handleCancel = async (commandId: string) => {
    try {
      await cancelCommand(commandId).unwrap();
    } catch (err) {
      console.error('Failed to cancel command:', err);
    }
  };

  const getStatusChip = (status: Command['status']) => {
    const statusConfig = {
      pending: { color: 'default' as const, label: 'Pending' },
      executing: { color: 'info' as const, label: 'Executing' },
      completed: { color: 'success' as const, label: 'Completed' },
      failed: { color: 'error' as const, label: 'Failed' },
      cancelled: { color: 'warning' as const, label: 'Cancelled' },
      expired: { color: 'error' as const, label: 'Expired' },
    };

    const config = statusConfig[status] || { color: 'default' as const, label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const calculateDuration = (command: Command): string => {
    if (!command.startedAt) return '-';
    const start = new Date(command.startedAt).getTime();
    // Still-running commands are measured up to when this list was fetched
    const end = command.completedAt ? new Date(command.completedAt).getTime() : fulfilledTimeStamp ?? start;
    const duration = (end - start) / 1000;
    return `${duration.toFixed(1)}s`;
  };

  // Get commands from API (already filtered by backend)
  const commands = data?.data || [];

  if (error) {
    // Handle specific error cases
    if ('status' in error && error.status === 404) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Device not found or commands are not available for this device.
          </Alert>
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            The device may have been removed or you may not have permission to view its commands.
          </Typography>
        </Box>
      );
    }

    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load command history. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters and Actions */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{
          alignItems: 'center'
        }}>
          <TextField

            placeholder="Search commands..."

            aria-label="Search commands"
            size="small"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }
            }}
          />
          <Stack direction="row" spacing={1}>
            <Chip
              label="All"
              onClick={() => handleStatusFilter(null)}
              color={!statusFilter ? 'primary' : 'default'}
            />
            <Chip
              label="Pending"
              onClick={() => handleStatusFilter('pending')}
              color={statusFilter === 'pending' ? 'primary' : 'default'}
            />
            <Chip
              label="Executing"
              onClick={() => handleStatusFilter('executing')}
              color={statusFilter === 'executing' ? 'primary' : 'default'}
            />
            <Chip
              label="Completed"
              onClick={() => handleStatusFilter('completed')}
              color={statusFilter === 'completed' ? 'primary' : 'default'}
            />
            <Chip
              label="Failed"
              onClick={() => handleStatusFilter('failed')}
              color={statusFilter === 'failed' ? 'primary' : 'default'}
            />
            <Chip
              label="Cancelled"
              onClick={() => handleStatusFilter('cancelled')}
              color={statusFilter === 'cancelled' ? 'primary' : 'default'}
            />
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setDpsEnvironmentOpen(true)}
          >
            DPS Environment
          </Button>
          {device && !device.gsmDisabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SignalCellularAltIcon />}
              onClick={() => setLteApnOpen(true)}
            >
              LTE APN
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => setSendCommandOpen(true)}
          >
            Send Command
          </Button>
        </Stack>
      </Box>

      {/* Commands Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead
            sx={{
              bgcolor: 'action.hover',
              '& .MuiTableCell-head': {
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                letterSpacing: 0,
                py: 2,
              },
            }}
          >
            <TableRow>
              <TableCell width={40} />
              <TableCell>Timestamp</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Command</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={8}>
                    <Skeleton variant="text" height={40} />
                  </TableCell>
                </TableRow>
              ))
            ) : commands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      py: 2
                    }}>
                    {searchQuery ? 'No commands match your search criteria' : 'No commands found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              commands.map((command) => (
                <React.Fragment key={command.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton

                        size="small"

                        onClick={() => toggleRowExpanded(command.id)}

                        aria-label={expandedRows.has(command.id) ? "Collapse row" : "Expand row"}

                      >

                        {expandedRows.has(command.id) ? (

                          <KeyboardArrowUpIcon />

                        ) : (

                          <KeyboardArrowDownIcon />

                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatTimestamp(command.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={command.type} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: fontFamilyMono,
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                        {command.command}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {command.createdBy || command.created_by ? (
                        <Stack direction="row" spacing={0.5} sx={{
                          alignItems: 'center'
                        }}>
                          <Typography variant="body2">
                            {command.createdBy || command.created_by}
                          </Typography>
                          <Chip
                            size="small"
                            label={(command.createdByType || command.created_by_type || 'system') === 'user' ? 'User' : 'System'}
                            color={(command.createdByType || command.created_by_type || 'system') === 'user' ? 'primary' : 'default'}
                            sx={{ height: 20 }}
                          />
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{
                          color: 'text.secondary'
                        }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(command.status)}</TableCell>
                    <TableCell>{calculateDuration(command)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {command.status === 'failed' && (
                          <IconButton

                            size="small"

                            onClick={() => handleRetry(command.id)}

                            title="Retry command"

                            aria-label="Retry command"

                          >

                            <PlayArrowIcon />
                          </IconButton>
                        )}
                        {(command.status === 'pending' || command.status === 'executing') && (
                          <IconButton

                            size="small"

                            onClick={() => handleCancel(command.id)}

                            title="Cancel command"

                            aria-label="Cancel command"

                          >

                            <CancelIcon />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                      <Collapse in={expandedRows.has(command.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            Command Details
                          </Typography>

                          <Stack spacing={2}>
                            {command.parameters && Object.keys(command.parameters).length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Parameters:
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      fontFamily: fontFamilyMono,
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                    {JSON.stringify(command.parameters, null, 2)}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {/* Special handling for SSH tunnel commands */}
                            {(command.command_type === 'setup_ssh_tunnel' || command.type === 'setup_ssh_tunnel') && (
                              <Box>
                                <Alert
                                  severity={
                                    command.status === 'completed' ? 'success' :
                                    command.status === 'executing' ? 'info' :
                                    command.status === 'pending' ? 'warning' :
                                    command.status === 'expired' ? 'error' :
                                    'error'
                                  }
                                  sx={{ mb: 2 }}
                                >
                                  <Typography variant="subtitle2" gutterBottom>
                                    🔒 SSH Tunnel - {
                                      command.status === 'completed' ? '✅ Active' :
                                      command.status === 'executing' ? '⏳ Establishing' :
                                      command.status === 'pending' ? '⏸️ Pending (may be active)' :
                                      command.status === 'expired' ? '❌ Expired' :
                                      '❌ Failed'
                                    }
                                  </Typography>
                                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                                    Port: <Chip
                                      label={(command.parameters as any)?.remote_port || 'Not Allocated'}
                                      color={command.status === 'completed' ? 'success' : 'default'}
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </Typography>
                                  <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'grey.100' }}>
                                    <Typography variant="body2" sx={{
                                      fontFamily: fontFamilyMono
                                    }}>
                                      ssh -p {(command.parameters as any)?.remote_port || '????'} cnservice@{(command.parameters as any)?.ssh_host || window.location.hostname}
                                    </Typography>
                                  </Paper>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      mt: 1,
                                      display: 'block'
                                    }}>
                                    <strong>Note:</strong> Authentication uses dynamic SSH keys (generated for each tunnel). Use the Web Terminal button above for direct access.
                                  </Typography>
                                  {(command.parameters as any)?.duration && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Duration: {(command.parameters as any).duration} seconds ({Math.round((command.parameters as any).duration / 60)} minutes)
                                    </Typography>
                                  )}
                                  {command.status === 'pending' && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 2,
                                        fontStyle: 'italic'
                                      }}>
                                      Note: If the device has connectivity, the tunnel may already be active even though the status shows pending.
                                    </Typography>
                                  )}
                                </Alert>
                              </Box>
                            )}

                            {/* Special handling for extend_ssh_tunnel commands */}
                            {(command.command_type === 'extend_ssh_tunnel' || command.type === 'extend_ssh_tunnel') && (
                              <Box>
                                <Alert
                                  severity={
                                    command.status === 'completed' ? 'success' :
                                    command.status === 'executing' ? 'info' :
                                    command.status === 'pending' ? 'warning' :
                                    'error'
                                  }
                                  sx={{ mb: 2 }}
                                >
                                  <Typography variant="subtitle2" gutterBottom>
                                    🔄 SSH Tunnel Extension - {
                                      command.status === 'completed' ? '✅ Extended' :
                                      command.status === 'executing' ? '⏳ Extending' :
                                      command.status === 'pending' ? '⏸️ Pending' :
                                      '❌ Failed'
                                    }
                                  </Typography>
                                  <Typography variant="body1" sx={{ mt: 1 }}>
                                    Port: <Chip
                                      label={(command.parameters as any)?.remote_port || 'Unknown'}
                                      color={command.status === 'completed' ? 'success' : 'default'}
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                    {(command.parameters as any)?.extension && (
                                      <>
                                        {' '} Extended by: <Chip
                                          label={`${(command.parameters as any).extension} min`}
                                          color="info"
                                          size="small"
                                          sx={{ ml: 1 }}
                                        />
                                      </>
                                    )}
                                  </Typography>
                                  {command.result && command.result.extended_until && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      Extended until: <strong>{new Date(command.result.extended_until).toLocaleString()}</strong>
                                    </Typography>
                                  )}
                                </Alert>
                              </Box>
                            )}

                            {command.result && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Result:
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      fontFamily: fontFamilyMono,
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                    {typeof command.result === 'string' ? command.result : JSON.stringify(command.result, null, 2)}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {command.output && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Output:
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      fontFamily: fontFamilyMono,
                                      whiteSpace: 'pre-wrap',
                                      overflowX: 'auto'
                                    }}>
                                    {command.output}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {command.error && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Error:
                                </Typography>
                                {/*
                                  `error.light` is pale in both themes, so the
                                  ink must be pinned dark — dark mode otherwise
                                  inherits white, at 2.15:1. grey.900 is the
                                  same #212121 in both (dark reuses the light
                                  ramp): 5.6:1 light, 7.0:1 dark.
                                */}
                                <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'grey.900' }}>
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      fontFamily: fontFamilyMono,
                                      whiteSpace: 'pre-wrap',
                                      overflowX: 'auto'
                                    }}>
                                    {command.error}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            <Stack direction="row" spacing={4}>
                              {command.startedAt && (
                                <Box>
                                  <Typography variant="caption" sx={{
                                    color: 'text.secondary'
                                  }}>
                                    Started At:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatTimestamp(command.startedAt)}
                                  </Typography>
                                </Box>
                              )}

                              {command.completedAt && (
                                <Box>
                                  <Typography variant="caption" sx={{
                                    color: 'text.secondary'
                                  }}>
                                    Completed At:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatTimestamp(command.completedAt)}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Send Command Dialog */}
      <SendCommandDialog
        open={sendCommandOpen}
        onClose={() => {
          setSendCommandOpen(false);
          refetch(); // Refresh commands list after sending
        }}
        deviceId={deviceId}
        deviceName={device?.name}
        sshTunnelPort={device?.ssh?.tunnelPort}
      />

      {/* DPS Environment Dialog */}
      {device && (
        <DPSEnvironmentDialog
          open={dpsEnvironmentOpen}
          onClose={() => {
            setDpsEnvironmentOpen(false);
            refetch(); // Refresh commands list after setting environment
          }}
          device={device}
        />
      )}

      {device && (
        <LteApnDialog
          open={lteApnOpen}
          onClose={() => {
            setLteApnOpen(false);
            refetch();
          }}
          device={device}
        />
      )}
    </Box>
  );
};

export default DeviceCommands;
